import type { ReactNode } from "react";

export interface StateBoundaryProps {
  loading?: boolean;
  error?: Error | null;
  empty?: boolean;
  /** Override the default Polish loader. */
  loadingSlot?: ReactNode;
  /** Caller-supplied empty-state content (usually Polish copy). */
  emptySlot?: ReactNode;
  /** Custom error renderer. Receives the error so the caller can format it. */
  errorSlot?: (err: Error) => ReactNode;
  /** Success content, rendered when none of the other states apply. */
  children: ReactNode;
}

/** Default Polish loader; overridable via `loadingSlot`. */
const DEFAULT_LOADING: ReactNode = <div className="ni-state ni-state--loading">Ładowanie…</div>;

/** Default Polish error copy; overridable via `errorSlot`. */
const DEFAULT_ERROR = (_err: Error): ReactNode => (
  <div className="ni-state ni-state--error" role="alert">
    Wystąpił błąd. Spróbuj ponownie.
  </div>
);

/**
 * Single async-surface primitive. Priority order: loading → error → empty →
 * success. Callers MUST wrap async UI in StateBoundary (no bare ternaries).
 * Custom errorSlot values are wrapped in role="alert" so accessibility holds
 * even if the caller forgets.
 */
export function StateBoundary({
  loading = false,
  error = null,
  empty = false,
  loadingSlot,
  emptySlot,
  errorSlot,
  children,
}: StateBoundaryProps): ReactNode {
  if (loading) return loadingSlot ?? DEFAULT_LOADING;
  if (error) {
    if (errorSlot) {
      return (
        <div role="alert" className="ni-state ni-state--error">
          {errorSlot(error)}
        </div>
      );
    }
    return DEFAULT_ERROR(error);
  }
  if (empty) return emptySlot ?? null;
  return children;
}
